import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "./Header";
import Footer from "./Footer";
import MobileCTA from "./MobileCTA";

// Context so any page can register its per-language alternate URLs,
// which the Header language switcher and SEO hreflang consume.
const AlternatesContext = createContext({ alternates: null, setAlternates: () => {} });

export function useAlternates() {
  return useContext(AlternatesContext);
}

// Pages call this in render/effect to publish their hreflang alternates.
export function useRegisterAlternates(altMap) {
  const { setAlternates } = useContext(AlternatesContext);
  const serialized = JSON.stringify(altMap || null);
  useEffect(() => {
    setAlternates(altMap || null);
    return () => setAlternates(null);
  }, [serialized]);
}

export default function Layout({ children }) {
  const [alternates, setAlternates] = useState(null);
  const location = useLocation();
  const lenisRef = useRef(null);

  // Smooth momentum scrolling with Lenis, respecting reduced motion.
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return undefined;

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Scroll to top on route change (and reset lenis position).
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo(0, 0);
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
  }, [location.pathname, location.hash]);

  return (
    <AlternatesContext.Provider value={{ alternates, setAlternates }}>
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
        <MobileCTA />
      </div>
    </AlternatesContext.Provider>
  );
}
